import { useNavigate } from 'react-router-dom'
import {
  PiCheckCircle,
  PiUsers,
  PiHandshake,
  PiPaperPlaneTilt,
  PiChat,
  PiTrendUp,
  PiLightning,
  PiPlus,
  PiClock,
} from 'react-icons/pi'

import { Alert } from 'components/FbUI/Alert'
import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'
import { cn } from 'components/UI/cn'
import type { Campaign } from 'models/campaign'
import { WORKFLOW_STATUS } from 'models/campaign'

type ChecklistState = 'todo' | 'pending' | 'done'

interface Props {
  campaign: Campaign
  campaignId: string
  hasSequence: boolean
}

export function CampaignDetailOverviewTab({
  campaign,
  campaignId,
  hasSequence,
}: Props) {
  const navigate = useNavigate()

  const hasTargets = campaign.contact_company_count > 0
  const hasContacts = campaign.contact_count > 0
  const hasLaunchDate = !!campaign.launch_date
  const hasContactsRequested =
    campaign.workflow_status !== WORKFLOW_STATUS.DRAFT
  const contactsState: ChecklistState = hasContacts
    ? 'done'
    : hasContactsRequested
      ? 'pending'
      : 'todo'

  const checklistItems: {
    label: string
    state: ChecklistState
    detail: string | undefined
  }[] = [
    {
      label: 'Campaign created',
      state: 'done',
      detail: undefined,
    },
    {
      label: 'Targets assigned',
      state: hasTargets ? 'done' : 'todo',
      detail: hasTargets
        ? `${campaign.contact_company_count} targets`
        : undefined,
    },
    {
      label: 'Contacts requested',
      state: contactsState,
      detail:
        contactsState === 'done'
          ? `${campaign.contact_count} contacts`
          : contactsState === 'pending'
            ? 'Waiting for contacts…'
            : undefined,
    },
    {
      label: 'Email sequence built',
      state: hasSequence ? 'done' : 'todo',
      detail: hasSequence ? 'Steps configured' : undefined,
    },
    {
      label: 'Projected launch date set',
      state: hasLaunchDate ? 'done' : 'todo',
      detail: hasLaunchDate
        ? new Date(campaign.launch_date!).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : undefined,
    },
  ]

  const completedCount = checklistItems.filter(
    (item) => item.state === 'done'
  ).length
  const progressPct = Math.round((completedCount / checklistItems.length) * 100)

  const statCards = [
    {
      label: 'Targets',
      value: hasTargets ? campaign.contact_company_count : '—',
      icon: TargetArrowIcon,
    },
    {
      label: 'Contacts',
      value: hasContacts ? campaign.contact_count : '—',
      icon: PiUsers,
    },
    { label: 'Deals', value: '—', icon: PiHandshake },
    { label: 'Emails Sent', value: '—', icon: PiPaperPlaneTilt },
    { label: 'Reply Rate', value: '—', icon: PiChat },
    { label: 'Interested Rate', value: '—', icon: PiTrendUp },
  ]

  const getNextStepsMessage = () => {
    if (!hasSequence) {
      return 'Create your email sequence to start reaching your targets.'
    }
    if (!hasContactsRequested) {
      return 'Request contacts to start your outreach.'
    }
    if (!hasLaunchDate) {
      return 'Set a projected launch date to finalize your campaign.'
    }
    return 'Review your setup and launch when ready.'
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-3 bg-surface rounded-lg border border-line p-3.5"
          >
            <div className="size-9 rounded-lg bg-brand-active/10 flex items-center justify-center shrink-0 text-brand-active">
              <stat.icon size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-fg-secondary truncate">{stat.label}</p>
              <p
                className={cn(
                  'text-lg font-bold leading-tight',
                  stat.value === '—' ? 'text-fg-muted' : 'text-fg'
                )}
              >
                {stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-surface rounded-lg border border-line p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base text-fg">Setup Progress</h3>
            <span className="text-xs font-medium text-brand-active bg-[#edfcf2] px-2 py-0.5 rounded-full">
              {completedCount}/{checklistItems.length}
            </span>
          </div>
          <div className="w-full h-2 bg-surface-alt rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-brand-bright rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="space-y-2.5">
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                {item.state === 'done' ? (
                  <PiCheckCircle className="size-4 text-brand-active shrink-0" />
                ) : item.state === 'pending' ? (
                  <PiClock className="size-4 text-warning shrink-0" />
                ) : (
                  <div className="size-4 rounded-full border-2 border-line shrink-0" />
                )}
                <span
                  className={cn(
                    'text-sm',
                    item.state === 'done' && 'text-fg',
                    item.state === 'pending' && 'text-fg-secondary',
                    item.state === 'todo' && 'text-fg-muted'
                  )}
                >
                  {item.label}
                </span>
                {item.detail && (
                  <span
                    className={cn(
                      'ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium tracking-[-0.2px]',
                      item.state === 'pending'
                        ? 'bg-warning-bg text-warning-strong'
                        : 'bg-surface-alt text-fg-secondary'
                    )}
                  >
                    {item.detail}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-line p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base text-fg">Email Sequence</h3>
            {hasSequence && (
              <button
                type="button"
                onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
                className="text-sm font-medium text-brand-bright hover:text-brand-active tracking-[-0.2px] transition-colors"
              >
                Edit Sequence
              </button>
            )}
          </div>

          {hasSequence ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-sm text-fg-secondary">Sequence configured.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="size-12 rounded-2xl bg-brand-active/10 flex items-center justify-center mb-3">
                <PiLightning className="size-6 text-brand-active" />
              </div>
              <p className="text-sm text-fg mb-1">No sequence yet</p>
              <p className="text-xs text-fg-secondary mb-4 max-w-[260px]">
                Build a multi-step email sequence to reach your targets.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
                className="flex items-center gap-2 bg-brand-bright hover:bg-brand-active text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                <PiPlus className="size-3.5" />
                Create Sequence
              </button>
            </div>
          )}
        </div>
      </div>

      {completedCount < checklistItems.length && (
        <Alert
          color="info"
          title="Next steps"
          description={getNextStepsMessage()}
        />
      )}
    </div>
  )
}
